import { webhookSocket } from "context/socket";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { listPublishes, retrySocialMediaBulkPublish } from "store/publish/actions";
import { upsertPublishItem } from "store/publish/slice";
import LocalStorage from "utils/LocalStorage";

import { SocialMediaPublishStatusContext } from "./context";

export const SocialMediaPublishProvider = ({ children, retrySocialMediaBulkPublish, upsertPublishItem }) => {
  const [publishItems, setPublishItems] = useState([]);

  const entityId = LocalStorage.getItem("entityId");

  const updatePublishItem = useCallback(
    async (data) => {
      try {
        const { id } = data || {};

        if (id) {
          upsertPublishItem({ data, publishId: id });
          setPublishItems((prevPublishItems) => {
            return prevPublishItems.map((group) => {
              const updatedPosts = group.posts.map((post) => {
                if (post?.id === id) {
                  return {
                    ...post,
                    status: data?.status === "completed" ? "success" : data?.status,
                    postUrl: data?.details?.postUrl || "",
                    error: data?.details?.errorMessage,
                  };
                }
                return post;
              });
              return {
                ...group,
                posts: updatedPosts,
              };
            });
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    [upsertPublishItem],
  );

  useEffect(() => {
    if (entityId) {
      const eventName = `publisher_social_media_publish_status_${entityId}`;

      webhookSocket.on(eventName, (data) => {
        updatePublishItem(data?.data?.publishRecord);
      });

      return () => {
        webhookSocket.off(eventName);
      };
    }
  }, [updatePublishItem, entityId]);

  const removeModal = (key) => {
    setPublishItems((prev) => prev.filter((group) => group.key !== key));
  };

  const retryPost = useCallback(
    async (groupKey, postId) => {
      try {
        // Find the publish group and post
        const publishGroup = publishItems?.find((group) => group?.key === groupKey);
        if (!publishGroup) {
          console.error("Publish group not found");
          return;
        }

        const post = publishGroup?.posts?.find((post) => post?.id === postId);
        if (!post) {
          console.error("Post not found in the specified group");
          return;
        }

        let data = null;
        let isSuccess = false;

        // Retry logic with error handling
        try {
          const response = await retrySocialMediaBulkPublish({ payload: { publishId: postId, entityId } });
          data = response?.payload?.data || response?.error;
          isSuccess = data?.success;
        } catch (error) {
          console.error("Error occurred during retrySocialPublishPost call:", error?.message || error);
          data = error;
        }

        // Update post with retry status and error details
        const updatedPost = {
          ...post,
          status: isSuccess ? "loading" : "failed",
          isRetryAllowed: false,
          error: isSuccess ? "" : data?.message || post?.error || "Unknown error occurred.",
        };

        // Update the posts array and publish items state
        const updatedPublishItems = publishItems?.map((group) =>
          group?.key === groupKey
            ? { ...group, posts: group?.posts.map((p) => (p?.id === postId ? updatedPost : p)) }
            : group,
        );

        setPublishItems(updatedPublishItems);
      } catch (error) {
        console.error("Error occurred in retryPost:", error?.message || error);
      }
    },
    [entityId, publishItems, retrySocialMediaBulkPublish, setPublishItems],
  );

  return (
    <SocialMediaPublishStatusContext.Provider value={{ publishItems, setPublishItems, removeModal, retryPost }}>
      {children}
    </SocialMediaPublishStatusContext.Provider>
  );
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = {
  listPublishes,
  retrySocialMediaBulkPublish,
  upsertPublishItem,
};

export default connect(mapStateToProps, mapDispatchToProps)(SocialMediaPublishProvider);
